import React, { createContext, useContext, useMemo } from "react";
import {
  getArticles,
  getProfile,
  getFeatured,
  getHighlights,
  getPullQuotes,
  getPublications,
  getCareer,
  getSiteSections,
} from "../lib/api";
import {
  articles as mockArticles,
  writer,
  aboutBio,
  focusAreas,
  featuredCover as mockFeaturedCover,
  highlights as mockHighlights,
  pullQuotes as mockPullQuotes,
  publications as mockPublications,
  careerHighlights as mockCareerHighlights,
} from "../mock";
import { defaultSiteSections } from "../lib/defaultSiteSections";
import useApiWithFallback from "../hooks/useApiWithFallback";

const SiteDataContext = createContext(null);

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const mockArticleBySlug = new Map(
  mockArticles.map((article) => [article.slug || slugify(article.title), article])
);

const normalizeArticle = (article, index) => {
  const slug = article.slug || slugify(article.title);
  const mockArticle = mockArticleBySlug.get(slug) || mockArticles[index] || {};

  return {
    ...mockArticle,
    ...article,
    id: article._id || article.id || mockArticle.id || slug,
    slug,
    image: article.imageUrl || article.image || mockArticle.image || "/og-image.png",
    imageUrl: article.imageUrl || article.image || mockArticle.image || "/og-image.png",
    imageAlt: article.imageAlt || article.title || mockArticle.title || "",
    imagePosition: article.imagePosition || mockArticle.imagePosition || "center",
    imageFit: article.imageFit || mockArticle.imageFit || "cover",
    accent: article.accent || mockArticle.accent || "black",
    rotation:
      typeof article.rotation === "number"
        ? article.rotation
        : typeof mockArticle.rotation === "number"
        ? mockArticle.rotation
        : 0,
    category: article.category || mockArticle.category || "Story",
    publication: article.publication || mockArticle.publication || "Mai El Mokadem",
    excerpt: article.excerpt || mockArticle.excerpt || "",
    externalUrl: article.externalUrl || mockArticle.externalUrl || "",
    featuredOnHomepage: article.featuredOnHomepage === true,
    sourceType: article.sourceType || mockArticle.sourceType || (article.externalUrl || mockArticle.externalUrl ? "external" : "internal"),
  };
};

const normalizeArticles = (apiArticles) => {
  if (!Array.isArray(apiArticles) || apiArticles.length === 0) {
    return mockArticles.map(normalizeArticle);
  }
  return apiArticles.map(normalizeArticle);
};

const normalizeProfile = (profile) => {
  if (!profile) {
    return { writer, aboutBio, focusAreas };
  }

  const instagram = profile.instagram || writer.instagram;
  const normalizedAboutBio = Array.isArray(profile.aboutBio)
    ? profile.aboutBio
    : profile.aboutBio
    ? [profile.aboutBio]
    : aboutBio;

  return {
    writer: {
      ...writer,
      name: profile.name || writer.name,
      role: profile.title || writer.role,
      email: profile.email || writer.email,
      instagram,
      instagramUrl: instagram ? `https://instagram.com/${instagram}` : writer.instagramUrl,
      bio: profile.bio || writer.bio,
    },
    aboutBio: normalizedAboutBio.length ? normalizedAboutBio : aboutBio,
    focusAreas:
      Array.isArray(profile.focusAreas) && profile.focusAreas.length
        ? profile.focusAreas
        : focusAreas,
  };
};

const normalizeFeatured = (apiFeatured) => {
  if (!apiFeatured || !apiFeatured.title) {
    return mockFeaturedCover;
  }
  return {
    ...mockFeaturedCover,
    title: apiFeatured.title || mockFeaturedCover.title,
    kicker: apiFeatured.subtitle || mockFeaturedCover.kicker,
    dek: apiFeatured.excerpt || mockFeaturedCover.dek,
    publication: apiFeatured.publication || mockFeaturedCover.publication,
    category: apiFeatured.category || mockFeaturedCover.category,
    image: apiFeatured.imageUrl || mockFeaturedCover.image,
    imageAlt: apiFeatured.imageAlt || apiFeatured.title || mockFeaturedCover.title,
    imagePosition: apiFeatured.imagePosition || "top",
    imageFit: apiFeatured.imageFit || "cover",
    externalUrl: apiFeatured.externalUrl || mockFeaturedCover.externalUrl || "",
  };
};

const normalizeHighlights = (apiHighlights) => {
  if (!Array.isArray(apiHighlights) || apiHighlights.length === 0) {
    return mockHighlights;
  }
  return apiHighlights.map((h, i) => ({
    id: h._id || `h-api-${i}`,
    stamp: h.metric || mockHighlights[i]?.stamp || "HIGHLIGHT",
    title: h.title || mockHighlights[i]?.title || "",
    body: h.description || mockHighlights[i]?.body || "",
    imageUrl: h.imageUrl || "",
    imageAlt: h.imageAlt || h.title || "",
    imagePosition: h.imagePosition || "center",
    imageFit: h.imageFit || "cover",
    value: h.value || "",
    sortOrder: h.sortOrder ?? i,
  }));
};

const normalizePullQuotes = (apiPullQuotes) => {
  if (!Array.isArray(apiPullQuotes) || apiPullQuotes.length === 0) {
    return mockPullQuotes;
  }
  return apiPullQuotes.map((q, i) => ({
    id: q._id || `pq-api-${i}`,
    text: q.quote || mockPullQuotes[i]?.text || "",
    attribution: q.source || mockPullQuotes[i]?.attribution || "",
    context: q.context || "",
    sortOrder: q.sortOrder ?? i,
  }));
};

const normalizePublications = (apiPublications) => {
  if (!Array.isArray(apiPublications) || apiPublications.length === 0) {
    return mockPublications;
  }
  return apiPublications.map((p) => (typeof p === "string" ? p : p.name || ""));
};

const normalizeCareer = (apiCareer) => {
  if (!Array.isArray(apiCareer) || apiCareer.length === 0) {
    return mockCareerHighlights;
  }
  return apiCareer.map((c, i) => ({
    year: c.year || "",
    title: c.title || "",
    body: c.description || mockCareerHighlights[i]?.body || "",
  }));
};

const normalizeSiteSections = (apiSections) => {
  const sections = Array.isArray(apiSections) && apiSections.length ? apiSections : defaultSiteSections;
  return [...sections].sort((a, b) => {
    if ((a.page || "") !== (b.page || "")) return String(a.page || "").localeCompare(String(b.page || ""));
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });
};

export const SiteDataProvider = ({ children }) => {
  const articleState = useApiWithFallback(getArticles, mockArticles);
  const profileState = useApiWithFallback(getProfile, null);
  const featuredState = useApiWithFallback(getFeatured, mockFeaturedCover);
  const highlightsState = useApiWithFallback(getHighlights, mockHighlights);
  const pullQuotesState = useApiWithFallback(getPullQuotes, mockPullQuotes);
  const publicationsState = useApiWithFallback(getPublications, mockPublications);
  const careerState = useApiWithFallback(getCareer, mockCareerHighlights);
  const siteSectionsState = useApiWithFallback(getSiteSections, defaultSiteSections);

  const value = useMemo(() => {
    const profile = normalizeProfile(profileState.data);

    return {
      articles: normalizeArticles(articleState.data),
      writer: profile.writer,
      aboutBio: profile.aboutBio,
      focusAreas: profile.focusAreas,
      featuredCover: normalizeFeatured(featuredState.data),
      highlights: normalizeHighlights(highlightsState.data),
      pullQuotes: normalizePullQuotes(pullQuotesState.data),
      publications: normalizePublications(publicationsState.data),
      careerHighlights: normalizeCareer(careerState.data),
      siteSections: normalizeSiteSections(siteSectionsState.data),
      loading:
        articleState.loading ||
        profileState.loading ||
        featuredState.loading ||
        highlightsState.loading ||
        pullQuotesState.loading ||
        publicationsState.loading ||
        careerState.loading ||
        siteSectionsState.loading,
      errors: {
        articles: articleState.error,
        profile: profileState.error,
        featured: featuredState.error,
        highlights: highlightsState.error,
        pullQuotes: pullQuotesState.error,
        publications: publicationsState.error,
        career: careerState.error,
        siteSections: siteSectionsState.error,
      },
      sources: {
        articles: articleState.source,
        profile: profileState.source,
        featured: featuredState.source,
        highlights: highlightsState.source,
        pullQuotes: pullQuotesState.source,
        publications: publicationsState.source,
        career: careerState.source,
        siteSections: siteSectionsState.source,
      },
    };
  }, [
    articleState,
    profileState,
    featuredState,
    highlightsState,
    pullQuotesState,
    publicationsState,
    careerState,
    siteSectionsState,
  ]);

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
};

export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error("useSiteData must be used within SiteDataProvider");
  }
  return context;
};
